import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../client';

export interface JobTranslation {
    locale: string;
    title: string;
    location?: string;
    department?: string;
    type?: string;
    description?: string;
}

export interface ApiJob {
    id: string;
    active: boolean;
    order: number;
    // Flattened (current locale)
    title?: string;
    location?: string;
    department?: string;
    type?: string;
    description?: string;
    // All translations
    translations: JobTranslation[];
    createdAt: string;
    updatedAt: string;
}

const jobKeys = {
    all: ['jobs'] as const,
    active: () => [...jobKeys.all, 'active'] as const,
    admin: () => [...jobKeys.all, 'admin'] as const,
    detail: (id: string) => [...jobKeys.all, 'detail', id] as const,
};

// Public Hook
export const useJobs = () => {
    const { i18n } = useTranslation();
    const locale = i18n.language;

    return useQuery({
        queryKey: [...jobKeys.active(), locale],
        queryFn: async () => {
            const { data } = await api.get<ApiJob[]>(`/api/jobs?locale=${locale}`);
            return data || [];
        },
    });
};

// Admin Hooks
export const useAdminJobs = () => {
    return useQuery({
        queryKey: jobKeys.admin(),
        queryFn: async () => {
            const { data } = await api.get<ApiJob[]>('/api/admin/jobs');
            return data || [];
        },
    });
};

export const useCreateJob = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (job: Partial<ApiJob>) => {
            const { data } = await api.post<ApiJob>('/api/admin/jobs', job);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: jobKeys.all });
        },
    });
};

export const useUpdateJob = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, ...job }: Partial<ApiJob> & { id: string }) => {
            const { data } = await api.put<ApiJob>(`/api/admin/jobs/${id}`, job);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: jobKeys.all });
        },
    });
};

export const useDeleteJob = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/api/admin/jobs/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: jobKeys.all });
        },
    });
};
